import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";
import type { WhatsappTemplateModel } from "../models/WhatsappTemplate/WhatsappTemplateModel";
import { WhatsappTemplateCategory, WhatsappTemplateStatus } from "../types";

export type UseWhatsappTemplatesParams = UsePaginatedRequestHookParams<{
  entityId: string;
  type?: WhatsappTemplateCategory;
  status?: WhatsappTemplateStatus;
  enabled?: boolean;
}>;

export type UseWhatsappTemplatesReturn =
  UsePaginatedRequestHookReturn<WhatsappTemplateModel> & {
    isInitialized: boolean;
  };

export const useWhatsappTemplates = ({
  entityId,
  type,
  status,
  enabled,
  skip = false,
}: UseWhatsappTemplatesParams): UseWhatsappTemplatesReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [templates, setTemplates] = useState<WhatsappTemplateModel[]>([]);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [fetchTemplates] = integrationsApi.useLazyGetWhatsappTemplatesQuery();

  const loadPage = useCallback(
    async (page?: string) => {
      const newRequestId = getUniqueId();
      requestId.current = newRequestId;

      try {
        setIsError(false);
        setIsLoading(true);

        const result = await fetchTemplates({
          entityId,
          params: {
            page,
            type,
            status,
            enabled,
          },
          meta: { silent: true },
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setTemplates((prev) => {
            if (!page) {
              return result.data;
            }

            const existingIds = new Set(prev.map((template) => template.id));
            const newTemplates = result.data.filter(
              (template) => !existingIds.has(template.id)
            );

            return [...prev, ...newTemplates];
          });
          setNextPage(result.next_page);
        }
      } catch {
        if (requestId.current !== newRequestId) return;
        setIsError(true);
      } finally {
        if (requestId.current === newRequestId) {
          setIsLoading(false);
          setIsInitialized(true);
        }
      }
    },
    [enabled, entityId, fetchTemplates, status, type]
  );

  const loadMore = useCallback(async () => {
    if (isLoading || !nextPage) return;
    await loadPage(nextPage);
  }, [isLoading, loadPage, nextPage]);

  const reset = useCallback(() => {
    setTemplates([]);
    setNextPage(null);
    setIsError(false);
    setIsInitialized(false);
    loadPage();
  }, [loadPage]);

  const retry = useCallback(async () => {
    if (templates.length === 0) {
      await loadPage();
      return;
    }

    await loadMore();
  }, [loadMore, loadPage, templates.length]);

  useEffect(() => {
    if (skip) return;
    reset();
  }, [reset, skip]);

  const hasMore = !!nextPage;
  const isLoadingMore = isLoading && templates.length > 0;
  const isLoadingMoreError = isError && templates.length > 0;

  return {
    data: templates,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    isInitialized,
    hasMore,
    reset,
    loadMore,
    retry,
  };
};
